import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";

import {
  useStoreDispatch,
  useStoreSelector
} from "../../../../../../hooks/StoreHooks";
import SideChoices from "./side_choices";
import { InstallationType } from "../components/forms/Pergola_type";
import { Aperture, ChiusuraType, TendeType, VetroType } from "../../../../../models/pergola_configuration";
import { usePergolaConfigurationBuilderActions } from "../../../../../../store/reducers/pergola_configuration_builder";
import { LatoPergola } from "../components/pergola-draw/Pergola";
import { getCurrentUser } from "../../../../../core/common_functions";
import { useConfigurationsServicePergola } from "../../../../../services/pergola_service";
import { useDialogs } from "../../../../../providers/dialogs_provider";

type FormValues = {
  tipo_pergola: InstallationType;
} & (
  | {
      tipo_chiusura:
        | ChiusuraType.vetrate_ad_impacchettamento_andromeda
        | ChiusuraType.vetrate_scorrevoli_maia;
      tipo_vetro: VetroType;
      coloreTelaioNsRal: string;
      coloreTelaioRal: string;
      sistema_chiusura?: string;
      accessori?: any[];
      apertura: Aperture;
    }
  | ({
      tipo_chiusura: ChiusuraType.tende_zip;
    } & TendeType)
);

export const ChiusuraPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {base, pergolaConfigurationBuilder } = useStoreSelector(store => store);
  const currentUser = getCurrentUser(base);
  const { setStepActive, setStepComplete } =
    usePergolaConfigurationBuilderActions();
  const dispatcher = useStoreDispatch();
  const cg = useConfigurationsServicePergola();
  const [isSubmit, setIsSubmit] = useState(false);
  const larghezzaSoletta = (pergolaConfigurationBuilder.configuration?.dimensioni.larghezza ?? 2000) / 1000;
  const lunghezzaSoletta = (pergolaConfigurationBuilder.configuration?.dimensioni.profondita ?? 4000) / 1000;
  let nstacchi = Math.floor(larghezzaSoletta / 4.1);
  const tolleranza = 1e-9;
  const dialogs = useDialogs();

  if (Math.abs(nstacchi * 4.1 - larghezzaSoletta) > tolleranza) {
      nstacchi += 1;
  }
  let nstacchiy = Math.floor(lunghezzaSoletta / 7) ;
  if (Math.abs(nstacchiy * 7 - lunghezzaSoletta) > tolleranza) {
      nstacchiy += 1;
  }

  const initialValues: FormValues = {
    tipo_chiusura: ChiusuraType.vetrate_ad_impacchettamento_andromeda,
    tipo_vetro: VetroType.vetro_temperato_10mm,
    coloreTelaioNsRal: "",
    coloreTelaioRal: "",
    sistema_chiusura: undefined,
    accessori: [],
    tipo_pergola: InstallationType.installazione_libera,
    apertura: Aperture.laterale
  };

  const formik = useFormik<FormValues>({
    initialValues,
    onSubmit: () => {}
  });

  useEffect(() => {
    if (!id || isNaN(Number(id)) || !pergolaConfigurationBuilder.configuration) {
      navigate("/configurations");
    } else {
      dispatcher(setStepActive({ stepSlug: "chiusure", isActive: true }));
    }
  }, [id, navigate, dispatcher, pergolaConfigurationBuilder.configuration, setStepActive]);

  useEffect(() => {
    if (
      formik.values.tipo_chiusura ===
        ChiusuraType.vetrate_ad_impacchettamento_andromeda ||
      formik.values.tipo_chiusura === ChiusuraType.vetrate_scorrevoli_maia
    ) {
      formik.setFieldValue("tipo_vetro", VetroType.vetro_temperato_10mm);
    } else if (formik.values.tipo_chiusura === ChiusuraType.tende_zip) {
      formik.setFieldValue("tipo_vetro", undefined);
      formik.setFieldValue("sistema_chiusura", undefined);
      formik.setFieldValue("apertura", undefined);
    }
  }, [formik.values.tipo_chiusura]);

  const submit = async () => {
    setIsSubmit(true);
    try {
      const filteredLati = pergolaConfigurationBuilder.lati.filter(lato => {
        return (lato.tipoLato.tipologia.tipo_chiusura !== ChiusuraType.nessuna && (lato.segmento <= nstacchi && (lato.direzione === "Fronte" || lato.direzione === 'Retro'))|| (lato.segmento <= nstacchiy && (lato.direzione === "Sinistra" || lato.direzione === 'Destra')));
      });
      const data: { chiusure: LatoPergola[] } = {
        chiusure: filteredLati as LatoPergola[]
      };
      if (pergolaConfigurationBuilder.configuration && data) {
        await cg.updateChiusure(
          currentUser,
          pergolaConfigurationBuilder.configuration.configurationId,
          data
        );
      }
      dispatcher(setStepComplete({ stepSlug: "chiusure", isComplete: true }));
      dispatcher(setStepActive({ stepSlug: "illuminazioni", isActive: true }));
      navigate(`/pergola-configurations/${id}/illuminazioni`);
    } catch (e: any) {
      setIsSubmit(false);
      if (typeof e == "string") {
        dialogs?.showError("Attenzione...", e);
      } else {
        if (e.severity == "error") {
          dialogs?.showError("Attenzione...", e.message);
        } else if (e.severity == "warning") {
          dialogs?.showWarning("Attenzione...", e.message);
        }
      }
    }
  };

  return (
    <div className="row h-full m-0">
      <div
      >
        <div className="p-4">
          <h5 className="text-primary">Chiusure</h5>

          <SideChoices />

          <div className="d-flex flex-row-reverse justify-content-between mt-4">
            <Button
              disabled={isSubmit}
              variant="contained"
              onClick={submit}
              className="ps-3 pe-1"
            >
              <span>Avanti</span>
              <NavigateNextIcon />
            </Button>

            <Button
              variant="contained"
              color="secondary"
              className="ps-1 pe-3"
              onClick={() =>{
                  dispatcher(setStepActive({ stepSlug: "aspetto", isActive: true }));
                  navigate(`/pergola-configurations/${id}/aspetto`)
              }}
            >
              <NavigateBeforeIcon />
              <span>Indietro</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
